import { useGLTF } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { Group } from 'three';
import { SelectedMaterial } from './Material-01';

type RemoteObjectProps = {
  url: string;
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number | [number, number, number];
};

export function RemoteObject(props: RemoteObjectProps) {
  const { url, position = [0, 0, 0], rotation = [0, 0, 0], scale = 1, ...rest } = props;
  const { scene } = useGLTF(url) as { scene: Group };
  const groupRef = useRef<Group>(null);

  const [hovered, setHovered] = useState(false);
  const [selected, setSelected] = useState(false);

  // Define 3 versions of the shader
  const defaultMaterial = useMemo(() => new SelectedMaterial(0xffffff), []);
  const hoverMaterial = useMemo(() => new SelectedMaterial(0xffffcc), []);
  const selectedMaterial = useMemo(() => new SelectedMaterial(0xffffff), []);

  // Animate current active material
  useFrame(() => {
    const now = performance.now() / 1000;
    const activeMaterial = selected ? selectedMaterial : hovered ? hoverMaterial : defaultMaterial;
    activeMaterial.update(now);
  });

  // Apply the correct shader to scene
  const shaderScene = useMemo(() => {
    const clone = scene.clone(true);
    clone.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        const mesh = child as THREE.Mesh;
        mesh.material = defaultMaterial;
        mesh.castShadow = true;
        mesh.receiveShadow = true;
      }
    });
    return clone;
  }, [scene]);

  // Dynamic material switching
  useFrame(() => {
    const material = selected ? selectedMaterial : hovered ? hoverMaterial : defaultMaterial;

    shaderScene.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        (child as THREE.Mesh).material = material;
      }
    });
  });

  return (
    <group
      ref={groupRef}
      position={position}
      scale={scale}
      castShadow
      receiveShadow
      rotation={rotation.map(THREE.MathUtils.degToRad) as [number, number, number]}
      onPointerOver={(e) => {
        e.stopPropagation();
        setHovered(true);
      }}
      onPointerOut={(e) => {
        e.stopPropagation();
        setHovered(false);
      }}
      onClick={(e) => {
        e.stopPropagation();
        setSelected((s) => !s);
      }}
      {...rest}
    >
      <primitive castShadow receiveShadow object={shaderScene} />
    </group>
  );
}
